import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { countAdsByTab, filterAdsByTab, getStatusBadge, normalizeStatus } from '../utils/adStatus';
import { formatMoney, formatNumber, formatPhone, formatDate } from '../utils/formatters';
import { AvailabilityBadge } from '../components/AvailabilityBadge';
import { AvailabilityActions } from '../components/AvailabilityActions';
import { StaleAdPrompt } from '../components/StaleAdPrompt';
import { 
  ArrowLeft, 
  Plus, 
  Eye, 
  Edit2, 
  Trash2, 
  MoreVertical,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  Package,
  Loader2,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Zap,
  EyeOff
} from 'lucide-react';
import AppHeader from '../components/AppHeader';
import useScrollRestore from '../hooks/useScrollRestore';
import { getAdCoverImage } from '../lib/adImages';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import './MyAds.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── Local icon / colour hint per canonical status ────────────────────────
// We intentionally do NOT duplicate the i18n label here. All label text
// flows through `getStatusBadge()` → `adStatus.*` keys, which is the single
// source of truth for status copy (see /utils/adStatus.js).
// This map only augments the shared badge with page-specific presentation
// (icon + legacy CSS colour class used by .status-badge.*).
const STATUS_ICON_BY_CANONICAL = {
  active:          { icon: CheckCircle, color: 'green' },
  draft:           { icon: Edit2,       color: 'gray' },
  pending_review:  { icon: Clock,       color: 'yellow' },
  hidden_by_user:  { icon: EyeOff,      color: 'red' },
  sold:            { icon: CheckCircle, color: 'blue' },
  rejected:        { icon: XCircle,     color: 'red' },
};

function resolveStatusDisplay(rawStatus) {
  const canonical = normalizeStatus(rawStatus);
  const visual = STATUS_ICON_BY_CANONICAL[canonical] || STATUS_ICON_BY_CANONICAL.draft;
  const shared = getStatusBadge(rawStatus);
  return { ...visual, labelKey: shared.labelKey };
}

function MyAds() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();
  const isArabic = i18n.language?.startsWith('ar');
  
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  // iter369 — deep-linkable tab (?tab=active from the Account overview card)
  const initialTab = new URLSearchParams(location.search).get('tab');
  const [activeTab, setActiveTab] = useState(
    ['all', 'active', 'pending', 'draft', 'hidden'].includes(initialTab) ? initialTab : 'all'
  );
  // User tab clicks sync to the URL (replace, no new history entry) so the
  // selected tab survives Back navigation from Ad Details / Edit Ad.
  const selectTab = useCallback((tab) => {
    setActiveTab(tab);
    const params = new URLSearchParams(location.search);
    params.set('tab', tab);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);
  useScrollRestore(!loading);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [adToDelete, setAdToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [boostEligibleOnly, setBoostEligibleOnly] = useState(false); // Filter for boost-eligible ads
  const [sellerStats, setSellerStats] = useState(null); // Unified stats from backend
  const [backendCounts, setBackendCounts] = useState(null); // Canonical tab counts from /api/ads/user/me
  // iter301 — My Business badges: per-ad business stats keyed by ad_id.
  const [bizStats, setBizStats] = useState({});
  // iter313 — Seller-facing share analytics keyed by ad_id (only
  // populated for ads the current user owns; never shown publicly).
  const [shareStats, setShareStats] = useState({});
  
  // Refs for scrolling
  const listRef = useRef(null);
  
  // Republish state
  const [republishDialogOpen, setRepublishDialogOpen] = useState(false);
  const [adToRepublish, setAdToRepublish] = useState(null);
  const [republishing, setRepublishing] = useState(false);

  // Tab scroll navigation state
  const tabsScrollerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  
  // Handle availability status change (update local state)
  const handleAvailabilityChange = useCallback((adId, newStatus) => {
    setAds(prevAds => prevAds.map(ad => 
      ad.ad_id === adId 
        ? { ...ad, availability_status: newStatus }
        : ad
    ));
    // Refetch stats to keep counts consistent
    if (user?.user_id) {
      fetch(`${API_URL}/api/seller/${user.user_id}/stats`)
        .then(r => r.ok ? r.json() : null)
        .then(stats => { if (stats) setSellerStats(stats); })
        .catch(() => {});
    }
  }, [user]);

  // ========================================
  // STALE ADS PROMPT (Phase 6)
  // ========================================
  const [staleAds, setStaleAds] = useState([]);
  
  const fetchStaleAds = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/users/me/stale-ads?days=14`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStaleAds(data.stale_ads || []);
      }
    } catch (err) {
      console.error('Failed to fetch stale ads:', err);
    }
  }, [token]);
  
  useEffect(() => {
    fetchStaleAds();
  }, [fetchStaleAds]);

  // ========================================
  // HANDLE QUERY PARAMS (tab, focus)
  // ========================================
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    const focusParam = params.get('focus');
    
    // Set tab from URL if valid
    if (tabParam && ['all', 'active', 'pending', 'draft', 'hidden'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
    
    // Handle boost focus
    if (focusParam === 'boost') {
      setBoostEligibleOnly(true);
      // Set to active tab for boost-eligible ads
      setActiveTab('active');
      // Scroll to list after content loads
      requestAnimationFrame(() => {
        setTimeout(() => {
          listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 300);
      });
    }
  }, [location.search]);

  // ========================================
  // RTL-SAFE SCROLL HELPERS
  // Handles different browser RTL scrollLeft behaviors
  // ========================================
  
  // Get document direction
  const getDir = useCallback(() => {
    return document.documentElement.dir || document.body.dir || 'ltr';
  }, []);

  // Get normalized scroll position (always 0 to max, consistent across RTL modes)
  const getScrollLeftN = useCallback((el) => {
    if (!el) return 0;
    const dir = getDir();
    if (dir !== 'rtl') return el.scrollLeft;

    const max = el.scrollWidth - el.clientWidth;
    const s = el.scrollLeft;

    // Chrome: scrollLeft goes negative in RTL (-max to 0)
    if (s < 0) return max + s;
    
    // Safari/Firefox: positive descending (max to 0)
    return max - s;
  }, [getDir]);

  // Set scroll position using normalized value
  const setScrollLeftN = useCallback((el, normalizedPos) => {
    if (!el) return;
    const dir = getDir();
    const max = el.scrollWidth - el.clientWidth;

    if (dir !== 'rtl') {
      el.scrollLeft = normalizedPos;
      return;
    }

    // Detect Chrome negative mode by checking current scrollLeft
    if (el.scrollLeft < 0 || (el.scrollLeft === 0 && max > 0)) {
      // Chrome: set to negative value
      el.scrollLeft = normalizedPos - max;
      return;
    }

    // Safari/Firefox: positive descending
    el.scrollLeft = max - normalizedPos;
  }, [getDir]);

  // Update chevron visibility based on normalized scroll position
  const updateChevrons = useCallback(() => {
    const el = tabsScrollerRef.current;
    if (!el) return;
    
    const max = el.scrollWidth - el.clientWidth;
    if (max <= 0) {
      // No overflow, hide both arrows
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    const pos = getScrollLeftN(el);
    const eps = 4; // tolerance

    setCanScrollLeft(pos > eps);
    setCanScrollRight(pos < max - eps);
  }, [getScrollLeftN]);

  // Scroll tabs - PREV goes left (decreases position), NEXT goes right (increases position)
  const scrollTabs = useCallback((direction) => {
    const el = tabsScrollerRef.current;
    if (!el) {
      console.log('No scroll element');
      return;
    }

    const max = el.scrollWidth - el.clientWidth;
    if (max <= 0) {
      console.log('No overflow to scroll');
      return;
    }

    // Calculate scroll step - at least 140px or 60% of visible width
    const step = Math.max(140, Math.round(el.clientWidth * 0.6));
    const currentPos = getScrollLeftN(el);

    // Calculate next position
    const nextPos = direction === 'prev' || direction === 'left'
      ? Math.max(0, currentPos - step)
      : Math.min(max, currentPos + step);

    console.log(`Scrolling ${direction}: current=${currentPos}, next=${nextPos}, max=${max}`);

    // Set the new position
    setScrollLeftN(el, nextPos);

    // Update arrows after a short delay
    setTimeout(updateChevrons, 150);
  }, [getScrollLeftN, setScrollLeftN, updateChevrons]);

  // Initialize chevron state and listeners
  useEffect(() => {
    // Initial update with small delay to ensure content is rendered
    const initialTimeout = setTimeout(updateChevrons, 50);
    
    const el = tabsScrollerRef.current;
    if (!el) return;
    
    el.addEventListener('scroll', updateChevrons, { passive: true });
    window.addEventListener('resize', updateChevrons);
    
    return () => {
      clearTimeout(initialTimeout);
      el.removeEventListener('scroll', updateChevrons);
      window.removeEventListener('resize', updateChevrons);
    };
  }, [updateChevrons]);

  // Fetch user's ads
  const fetchMyAds = useCallback(async () => {
    if (!token || !user) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      // Fetch ads AND stats in parallel — stats are the SINGLE SOURCE OF TRUTH for counts
      const [adsRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/ads/user/me`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        }),
        fetch(`${API_URL}/api/seller/${user.user_id}/stats`),
      ]);
      
      if (adsRes.ok) {
        const data = await adsRes.json();
        setAds(data.ads || []);
        // Bug fix 2026-06-01h: backend now returns canonical `counts` block
        // computed from get_seller_stats() — uncapped, identical to the
        // numbers used by Account overview, Seller Profile, etc. We mirror
        // it into local state so the tab badges always match the rest of
        // the app even when the paginated `ads` list is capped.
        if (data.counts) {
          setBackendCounts(data.counts);
        }
        // iter301 — fetch My Business per-ad stats so each card can show
        // stock / sold / revenue badges. Best-effort; failure is silent.
        try {
          const bizRes = await fetch(`${API_URL}/api/business/ads/stats`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (bizRes.ok) {
            const bizJson = await bizRes.json();
            const map = {};
            (bizJson.stats || []).forEach(s => { map[s.ad_id] = s; });
            setBizStats(map);
          }
        } catch (_bizErr) { /* ignored */ }
        // iter313 — fetch the seller's share analytics (one call,
        // covers every ad the user owns). Failure is silent — the
        // analytics card simply won't render. Never shown to non-owners.
        try {
          const shareRes = await fetch(`${API_URL}/api/ads/share-stats/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (shareRes.ok) {
            const shareJson = await shareRes.json();
            const map = {};
            (shareJson.stats || []).forEach(s => { map[s.ad_id] = s; });
            setShareStats(map);
          }
        } catch (_shareErr) { /* ignored */ }
      } else {
        console.error('Failed to fetch ads');
        toast.error(isArabic ? 'فشل تحميل الإعلانات' : 'Failed to load ads');
      }
      
      if (statsRes.ok) {
        const stats = await statsRes.json();
        setSellerStats(stats);
      }
    } catch (error) {
      console.error('Error fetching ads:', error);
      toast.error(isArabic ? 'حدث خطأ' : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [token, user, isArabic]);

  useEffect(() => {
    fetchMyAds();
  }, [fetchMyAds]);

  // Filter ads by tab using shared utility (single source of truth)
  let filteredAds = filterAdsByTab(ads, activeTab);
  
  // Apply boost-eligible filter if active
  // Boost eligible: published status, not expired, not already boosted
  if (boostEligibleOnly) {
    filteredAds = filteredAds.filter(ad => {
      const isPublished = ad.status === 'published' || ad.status === 'active';
      const notBoosted = !ad.is_boosted;
      // Check if not expired (created within last 30 days or has no expiry)
      const createdAt = new Date(ad.created_at);
      const now = new Date();
      const daysSinceCreated = Math.floor((now - createdAt) / (1000 * 60 * 60 * 24));
      const notExpired = daysSinceCreated < 30;
      return isPublished && notBoosted && notExpired;
    });
  }

  // Counts (2026-06-01h)
  //
  // Priority: backend `counts` block (uncapped, single source of truth shared
  // with Account overview & Seller Profile) → client-side fallback derived
  // from the paginated `ads` array.
  //
  // The fallback is only used during the brief window before /api/ads/user/me
  // returns. After that, the badges read backendCounts so they exactly
  // match the numbers shown on every other page in the app.
  const clientCounts = countAdsByTab(ads);
  const counts = backendCounts || clientCounts;

  // Handle delete ad
  const handleDeleteAd = async () => {
    if (!adToDelete) return;
    
    try {
      setDeleting(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ads/${adToDelete.ad_id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.ok) {
        toast.success(isArabic ? 'تم حذف الإعلان' : 'Ad deleted successfully');
        // Refetch ads AND stats to ensure consistent counts
        fetchMyAds();
      } else {
        toast.error(isArabic ? 'فشل حذف الإعلان' : 'Failed to delete ad');
      }
    } catch (error) {
      console.error('Error deleting ad:', error);
      toast.error(isArabic ? 'حدث خطأ' : 'An error occurred');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setAdToDelete(null);
    }
  };

  // Handle republish ad
  const handleRepublishAd = async () => {
    if (!adToRepublish) return;
    
    try {
      setRepublishing(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ads/${adToRepublish.ad_id}/republish`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.ok) {
        toast.success(isArabic ? 'تم إعادة نشر الإعلان وهو الآن قيد المراجعة' : 'Ad republished and is now pending review');
        // Refetch ads AND stats to ensure consistent counts
        fetchMyAds();
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.detail || (isArabic ? 'فشل إعادة نشر الإعلان' : 'Failed to republish ad'));
      }
    } catch (error) {
      console.error('Error republishing ad:', error);
      toast.error(isArabic ? 'حدث خطأ في الاتصال' : 'Connection error');
    } finally {
      setRepublishing(false);
      setRepublishDialogOpen(false);
      setAdToRepublish(null);
    }
  };

  // Check if ad can be republished (hidden, draft, rejected)
  const canRepublishAd = (status) => {
    const s = (status || '').toLowerCase();
    return ['hidden', 'hidden_auto', 'draft', 'rejected'].includes(s);
  };

  // Get image URL helper
  // Canonical image extractor — replaces the old per-page logic that only
  // returned bare strings. Now reads from images/imageUrls/photos/thumbnail,
  // resolves relative /uploads/ paths, dedupes, and matches AdDetails 1:1.
  // Returns '' when no image — the <img onError> fallback handles failures.
  const getImageUrl = (image, fallbackAd) => {
    // Backward compat with the existing call signature
    if (image && typeof image === 'object' && image.images) {
      return getAdCoverImage(image);
    }
    if (fallbackAd) return getAdCoverImage(fallbackAd);
    // Single-image path used by `<img src={getImageUrl(ad.images?.[0])}>`
    if (!image) return '';
    return getAdCoverImage({ images: [image] });
  };

  // Get language for formatters
  const lang = i18n.language || 'en';

  // Redirect to login if not authenticated
  if (!user || !token) {
    return (
      <div className="my-ads-page">
        <div className="my-ads-auth-required">
          <Package size={48} />
          <h2>{isArabic ? 'يرجى تسجيل الدخول' : 'Please log in'}</h2>
          <p>{isArabic ? 'يجب تسجيل الدخول لعرض إعلاناتك' : 'You need to log in to view your ads'}</p>
          <button className="login-btn" onClick={() => navigate('/login')}>
            {isArabic ? 'تسجيل الدخول' : 'Log in'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="my-ads-page" dir={isArabic ? 'rtl' : 'ltr'}>
      {/* Stale Ad Prompt Modal */}
      <StaleAdPrompt 
        ads={staleAds} 
        locale={isArabic ? 'ar' : 'en'}
        onRefresh={() => {
          fetchMyAds();
          fetchStaleAds();
        }}
      />
      
      {/* Header */}
      <AppHeader
        title={isArabic ? 'إعلاناتي' : 'My Ads'}
        backFallback="/account"
        testId="my-ads-header"
        rightSlot={
          <button
            className="add-ad-btn"
            onClick={() => navigate('/add-ad')}
            aria-label={isArabic ? 'إضافة إعلان' : 'Add ad'}
            data-testid="my-ads-add-btn"
          >
            <Plus size={20} />
          </button>
        }
      />

      {/* Tabs with Navigation Arrows */}
      <div className="tabs-wrapper">
        {/* Left Chevron - points outward (left in LTR, right icon in RTL) */}
        {canScrollLeft && (
          <>
            <div className="tabs-fade tabs-fade-left" />
            <button 
              className="tabs-chevron tabs-chevron-left"
              onClick={() => scrollTabs('left')}
              aria-label={isArabic ? 'السابق' : 'Previous'}
            >
              {isArabic ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            </button>
          </>
        )}
        
        {/* Scrollable Tabs */}
        <div className="my-ads-tabs" ref={tabsScrollerRef}>
          <button 
            className={`tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => selectTab('all')}
          >
            {isArabic ? 'الكل' : 'All'}
            <span className="tab-count">{counts.all}</span>
          </button>
          <button 
            className={`tab ${activeTab === 'active' ? 'active' : ''}`}
            onClick={() => selectTab('active')}
          >
            {isArabic ? 'نشط' : 'Active'}
            <span className="tab-count">{counts.active}</span>
          </button>
          <button 
            className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => selectTab('pending')}
          >
            {t('myAds.statusUnderReview')}
            <span className="tab-count">{counts.pending}</span>
          </button>
          <button 
            className={`tab ${activeTab === 'draft' ? 'active' : ''}`}
            onClick={() => selectTab('draft')}
          >
            {isArabic ? 'مسودة' : 'Draft'}
            <span className="tab-count">{counts.draft}</span>
          </button>
          <button 
            className={`tab ${activeTab === 'hidden' ? 'active' : ''}`}
            onClick={() => selectTab('hidden')}
          >
            {isArabic ? 'مخفي' : 'Hidden'}
            <span className="tab-count">{counts.hidden}</span>
          </button>
        </div>
        
        {/* Right Chevron - points outward (right in LTR, left icon in RTL) */}
        {canScrollRight && (
          <>
            <div className="tabs-fade tabs-fade-right" />
            <button 
              className="tabs-chevron tabs-chevron-right"
              onClick={() => scrollTabs('right')}
              aria-label={isArabic ? 'التالي' : 'Next'}
            >
              {isArabic ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
            </button>
          </>
        )}
      </div>

      {/* Content */}
      <div className="my-ads-content" ref={listRef}>
        {/* Boost filter chip when active */}
        {boostEligibleOnly && (
          <div className="boost-filter-chip">
            <Zap size={14} />
            <span>{isArabic ? 'إعلانات قابلة للترويج' : 'Boost eligible'}</span>
            <button 
              className="boost-filter-clear"
              onClick={() => setBoostEligibleOnly(false)}
              aria-label={isArabic ? 'إزالة الفلتر' : 'Clear filter'}
            >
              ×
            </button>
          </div>
        )}
        
        {loading ? (
          <div className="my-ads-loading">
            <Loader2 size={32} className="animate-spin" />
            <p>{isArabic ? 'جاري التحميل...' : 'Loading...'}</p>
          </div>
        ) : ads.length === 0 ? (
          /* TRUE EMPTY: User has no ads at all */
          <div className="my-ads-empty">
            <Package size={56} strokeWidth={1.5} />
            <h3>{isArabic ? 'لا توجد إعلانات بعد' : 'No ads yet'}</h3>
            <p>
              {isArabic 
                ? 'ابدأ ببيع منتجاتك أو خدماتك الآن'
                : 'Start selling your products or services now'
              }
            </p>
            <button className="add-first-ad-btn" onClick={() => navigate('/add-ad')}>
              <Plus size={18} />
              {isArabic ? 'أضف إعلانك الأول' : 'Add your first ad'}
            </button>
          </div>
        ) : filteredAds.length === 0 ? (
          /* FILTER EMPTY: User has ads but none match current filter */
          <div className="my-ads-empty filter-empty">
            {boostEligibleOnly ? (
              /* Boost filter specific empty state */
              <>
                <Zap size={56} strokeWidth={1.5} className="empty-icon-boost" />
                <h3>{isArabic ? 'لا توجد إعلانات مؤهلة للترويج' : 'No boost-eligible ads'}</h3>
                <p>
                  {isArabic 
                    ? 'انشر إعلانك أولاً أو أزل الفلتر لعرض كل الإعلانات'
                    : 'Try publishing an ad first, or remove the filter to see all ads'
                  }
                </p>
                <button 
                  className="show-all-ads-btn" 
                  onClick={() => {
                    setBoostEligibleOnly(false);
                    selectTab('all');
                  }}
                >
                  {isArabic ? 'عرض كل الإعلانات' : 'Show all ads'}
                </button>
              </>
            ) : (
              /* Generic tab filter empty state */
              <>
                <Package size={56} strokeWidth={1.5} />
                <h3>{isArabic ? 'لا توجد إعلانات في هذه الفئة' : 'No ads in this category'}</h3>
                <p>
                  {isArabic 
                    ? 'جرب تصفية أخرى أو أضف إعلان جديد'
                    : 'Try a different filter or add a new ad'
                  }
                </p>
                <button 
                  className="show-all-ads-btn" 
                  onClick={() => selectTab('all')}
                >
                  {isArabic ? 'عرض كل الإعلانات' : 'Show all ads'}
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="my-ads-list">
            {filteredAds.map(ad => {
              const statusConfig = resolveStatusDisplay(ad.status);
              const StatusIcon = statusConfig.icon;
              const isDraft = ad.status === 'draft';
              const isBookListing = ad.is_book_listing;
              
              // Click handler: book listings go to book detail, drafts to edit, others to ad view.
              // We pass returnTo: '/my-ads' via route state so the destination's
              // back button takes the user back to My Ads, not to its own
              // section list (Student Hub Books / Shop). Fix 2026-06-01e.
              const handleAdClick = () => {
                if (isBookListing) {
                  navigate(`/student-hub/books/${ad.book_id || ad.ad_id}`, { state: { returnTo: '/my-ads' } });
                } else if (isDraft) {
                  navigate(`/edit-ad/${ad.ad_id}`, { state: { from: '/my-ads', returnTo: '/my-ads' } });
                } else {
                  navigate(`/ad/${ad.ad_id}`, { state: { returnTo: '/my-ads' } });
                }
              };
              
              // Edit handler for book listings
              const handleEditClick = () => {
                if (isBookListing) {
                  navigate(`/student-hub/books/${ad.book_id || ad.ad_id}`, { state: { returnTo: '/my-ads' } });
                } else {
                  navigate(`/edit-ad/${ad.ad_id}`, { state: { from: '/my-ads', returnTo: '/my-ads' } });
                }
              };
              
              // View handler
              const handleViewClick = () => {
                if (isBookListing) {
                  navigate(`/student-hub/books/${ad.book_id || ad.ad_id}`, { state: { returnTo: '/my-ads' } });
                } else {
                  navigate(`/ad/${ad.ad_id}`, { state: { returnTo: '/my-ads' } });
                }
              };
              
              return (
                <div key={ad.ad_id} className={`my-ad-card ${['sold', 'expired', 'unavailable'].includes(ad.availability_status) ? 'unavailable-card' : ''}`} data-testid={`my-ad-${ad.ad_id}`}>
                  {/* Thumbnail */}
                  <div 
                    className="my-ad-thumb"
                    onClick={handleAdClick}
                  >
                    <img 
                      src={getAdCoverImage(ad) || '/placeholder.svg'} 
                      alt={ad.title}
                      onError={(e) => { e.target.src = '/placeholder.svg'; }}
                    />
                    <span className={`status-badge ${statusConfig.color}`}>
                      <StatusIcon size={12} />
                      {t(statusConfig.labelKey) || ad.status}
                    </span>
                    {/* Availability Badge (if not available) */}
                    {ad.availability_status && ad.availability_status !== 'available' && (
                      <div className="availability-badge-container">
                        <AvailabilityBadge 
                          status={ad.availability_status} 
                          reserved_until={ad.reserved_until}
                          size="sm"
                          showStaleHint={false}
                        />
                      </div>
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="my-ad-content" onClick={handleAdClick}>
                    <h3 className="my-ad-title">{ad.title}</h3>
                    {isBookListing && (
                      <span className="book-listing-badge" data-testid={`book-badge-${ad.ad_id}`}>
                        {isArabic ? 'كتب وملخصات' : 'Books'}
                      </span>
                    )}
                    <p className="my-ad-price">
                      {formatMoney({ amount: ad.price, currency: ad.currency || 'SDG', lang }) 
                        || (isArabic ? 'السعر عند الطلب' : 'Price on request')}
                    </p>
                    <div className="my-ad-meta">
                      <span className="my-ad-views">
                        <Eye size={14} />
                        {formatNumber({ value: ad.viewCount || ad.views || 0, lang })}
                      </span>
                      {(ad.seller_phone || ad.seller_whatsapp) && (
                        <span className="my-ad-phone">
                          {formatPhone({ phone: ad.seller_phone || ad.seller_whatsapp, lang })}
                        </span>
                      )}
                      <span className="my-ad-date">
                        {formatDate({ date: ad.created_at, lang })}
                      </span>
                    </div>
                    {/* iter301 — My Business badges row. Only renders
                        when there is actual data; pure additive. */}
                    {(() => {
                      const bs = bizStats[ad.ad_id];
                      if (!bs) return null;
                      const showStock = bs.is_product && typeof bs.stock_remaining === 'number';
                      const showSold = bs.sold_count > 0;
                      const showRevenue = bs.revenue > 0;
                      const showLeads = (bs.lead_count || 0) > 0;
                      const showSaves = (bs.save_count || 0) > 0;
                      if (!showStock && !showSold && !showRevenue && !showLeads && !showSaves) return null;
                      return (
                        <div className="my-ad-biz-badges" data-testid={`my-ad-biz-${ad.ad_id}`}>
                          {showStock && (
                            <span className={`my-ad-biz-badge ${bs.stock_remaining === 0 ? 'red' : bs.stock_remaining <= (bs.low_stock_threshold ?? 3) ? 'yellow' : 'green'}`}>
                              {isArabic ? `المخزون: ${bs.stock_remaining}` : `Stock: ${bs.stock_remaining}`}
                            </span>
                          )}
                          {showSold && (
                            <span className="my-ad-biz-badge gray">
                              {isArabic ? `المباع: ${bs.sold_count}` : `Sold: ${bs.sold_count}`}
                            </span>
                          )}
                          {showRevenue && (
                            <span className="my-ad-biz-badge blue">
                              {isArabic ? `الإيراد: ${Math.round(bs.revenue).toLocaleString('ar-EG')} ${bs.currency}` : `Revenue: ${Math.round(bs.revenue).toLocaleString('en-US')} ${bs.currency}`}
                            </span>
                          )}
                          {showLeads && (
                            <span className="my-ad-biz-badge purple">
                              {isArabic ? `رسائل: ${bs.lead_count}` : `Leads: ${bs.lead_count}`}
                            </span>
                          )}
                          {showSaves && (
                            <span className="my-ad-biz-badge gray">
                              {isArabic ? `حفظ: ${bs.save_count}` : `Saves: ${bs.save_count}`}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                    {/* iter313 — Seller-only share analytics card.
                        Always renders for owned ads (gives clear "no
                        shares yet" feedback). Never visible to public
                        buyers since this lives inside My Ads which
                        requires auth + ownership filter. */}
                    {(() => {
                      const stats = shareStats[ad.ad_id];
                      if (!stats) return null;
                      const total = stats.total_shares || 0;
                      const topPlatform = stats.top_platform;
                      return (
                        <div
                          className="my-ad-share-card"
                          data-testid={`share-stats-${ad.ad_id}`}
                          data-total-shares={total}
                          data-top-platform={topPlatform || ''}
                        >
                          <span
                            className="my-ad-share-count"
                            data-testid={`share-count-${ad.ad_id}`}
                          >
                            {total === 0
                              ? t('adshar.noSharesYet')
                              : t('adshar.sharedTimes', { count: total })}
                          </span>
                          {topPlatform && (
                            <span
                              className="my-ad-share-top"
                              data-testid={`share-top-${ad.ad_id}`}
                            >
                              {t('adshar.topPlatform')}:{' '}
                              {t(`adshar.platform.${topPlatform}`)}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                    {/* P2.1 — Show rejection reason directly on the card so
                        sellers know exactly why their ad was rejected. */}
                    {ad.status === 'rejected' && ad.rejection_reason && (
                      <div className="my-ad-rejection-reason" data-testid={`rejection-reason-${ad.ad_id}`}>
                        <span className="my-ad-rejection-reason__label">
                          {t('myAds.rejectionReasonLabel')}:
                        </span>{' '}
                        <span>{ad.rejection_reason}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="my-ad-actions">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="actions-trigger" data-testid={`ad-actions-${ad.ad_id}`}>
                          <MoreVertical size={18} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {/* View option - only show for non-draft ads */}
                        {!isDraft && (
                          <DropdownMenuItem onClick={handleViewClick}>
                            <Eye size={16} />
                            {isArabic ? 'عرض' : 'View'}
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={handleEditClick}>
                          <Edit2 size={16} />
                          {isArabic ? 'تعديل' : 'Edit'}
                        </DropdownMenuItem>
                        {/* Republish option - only for hidden/draft/rejected ads */}
                        {canRepublishAd(ad.status) && (
                          <DropdownMenuItem 
                            className="republish-item"
                            onClick={() => {
                              setAdToRepublish(ad);
                              setRepublishDialogOpen(true);
                            }}
                          >
                            <RefreshCw size={16} />
                            {isArabic ? 'إعادة نشر' : 'Republish'}
                          </DropdownMenuItem>
                        )}
                        
                        {/* Availability Actions Separator */}
                        {!isDraft && ad.status === 'published' && (
                          <>
                            <DropdownMenuSeparator />
                            {/* Quick availability actions for published ads */}
                            {ad.availability_status !== 'sold' && (
                              <DropdownMenuItem 
                                className="sold-item"
                                onClick={() => {
                                  // Use AvailabilityActions API directly
                                  fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ads/${ad.ad_id}/mark-sold`, {
                                    method: 'POST',
                                    headers: { 'Authorization': `Bearer ${token}` }
                                  }).then(res => {
                                    if (res.ok) {
                                      handleAvailabilityChange(ad.ad_id, 'sold');
                                      toast.success(t('availability.marked_sold'));
                                    }
                                  });
                                }}
                              >
                                <CheckCircle size={16} />
                                {t('availability.mark_sold')}
                              </DropdownMenuItem>
                            )}
                            {ad.availability_status === 'sold' && (
                              <DropdownMenuItem 
                                className="available-item"
                                onClick={() => {
                                  fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ads/${ad.ad_id}/mark-available`, {
                                    method: 'POST',
                                    headers: { 'Authorization': `Bearer ${token}` }
                                  }).then(res => {
                                    if (res.ok) {
                                      handleAvailabilityChange(ad.ad_id, 'available');
                                      toast.success(t('availability.marked_available'));
                                    }
                                  });
                                }}
                              >
                                <RefreshCw size={16} />
                                {t('availability.mark_available')}
                              </DropdownMenuItem>
                            )}
                            {ad.availability_status !== 'sold' && (
                              <DropdownMenuItem 
                                className="renew-item"
                                onClick={() => {
                                  fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ads/${ad.ad_id}/renew`, {
                                    method: 'POST',
                                    headers: { 'Authorization': `Bearer ${token}` }
                                  }).then(res => {
                                    if (res.ok) {
                                      handleAvailabilityChange(ad.ad_id, 'available');
                                      toast.success(t('availability.renewed_success'));
                                    }
                                  });
                                }}
                              >
                                <Zap size={16} />
                                {t('availability.renew')}
                              </DropdownMenuItem>
                            )}
                          </>
                        )}
                        
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          className="delete-item"
                          onClick={() => {
                            setAdToDelete(ad);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 size={16} />
                          {isArabic ? 'حذف' : 'Delete'}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isArabic ? 'حذف الإعلان' : 'Delete Ad'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isArabic 
                ? `هل أنت متأكد من حذف "${adToDelete?.title}"؟ لا يمكن التراجع عن هذا الإجراء.`
                : `Are you sure you want to delete "${adToDelete?.title}"? This action cannot be undone.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>
              {isArabic ? 'إلغاء' : 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAd}
              disabled={deleting}
              className="delete-confirm-btn"
            >
              {deleting ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Trash2 size={16} />
              )}
              {isArabic ? 'حذف' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Republish Confirmation Dialog */}
      <AlertDialog open={republishDialogOpen} onOpenChange={setRepublishDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isArabic ? 'إعادة نشر الإعلان' : 'Republish Ad'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isArabic 
                ? `هل تريد إعادة نشر "${adToRepublish?.title}"؟ سيتم إرسال الإعلان للمراجعة قبل نشره.`
                : `Do you want to republish "${adToRepublish?.title}"? The ad will be submitted for review before being published.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={republishing}>
              {isArabic ? 'إلغاء' : 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRepublishAd}
              disabled={republishing}
              className="republish-confirm-btn"
            >
              {republishing ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <RefreshCw size={16} />
              )}
              {isArabic ? 'إعادة نشر' : 'Republish'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default MyAds;
